import React, { useState, useEffect } from 'react';
import { Search, User, Users, Check } from 'lucide-react';
import { useMessaging } from '../contexts/MessagingContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import EnhancedModal, { ModalHeader, ModalBody, ModalFooter, ModalButton } from './EnhancedModal';
import { useTheme } from '../contexts/ThemeContext';

const CreateGroupModal = ({ onClose, currentUser, db }) => {
  const { createGroup } = useMessaging();
  const { theme } = useTheme();
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Récupérer les employés disponibles ET les bénéficiaires-utilisateurs avec les noms des rôles
  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        // Récupérer tous les rôles depuis advanced_roles
        const rolesSnapshot = await getDocs(collection(db, 'advanced_roles'));
        const rolesMap = new Map();
        rolesSnapshot.forEach(doc => {
          const roleData = doc.data();
          rolesMap.set(doc.id, roleData.name || doc.id);
        });

        const employees = [];

        // Récupérer les intervenants (employés)
        const employeesQuery = query(collection(db, 'employees'));
        const employeesSnapshot = await getDocs(employeesQuery);

        console.log('Employés récupérés pour groupe:', employeesSnapshot.size);

        // Créer la Map des bénéficiaires-utilisateurs (clientId -> employeeId)
        const beneficiaireUserMap = new Map();
        employeesSnapshot.forEach(doc => {
          const employeeData = doc.data();
          if (employeeData.email && employeeData.email.startsWith('client_')) {
            // Extraire l'ID client depuis l'email: client_e4eqcgmsooilgnrkxol5@agentime.fr -> e4eqcgmsooilgnrkxol5
            const clientId = employeeData.email.replace('client_', '').split('@')[0];
            console.log('✅ Bénéficiaire-Utilisateur détecté (CreateGroupModal):', doc.id, employeeData.email, '-> clientId:', clientId);
            beneficiaireUserMap.set(clientId.toLowerCase(), doc.id);
          }
        });
        console.log('📊 Total bénéficiaires-utilisateurs détectés (CreateGroupModal):', beneficiaireUserMap.size);

        // Ajouter les employés normaux (intervenants)
        employeesSnapshot.forEach(doc => {
          const employeeData = doc.data();
          console.log('Employé pour groupe:', doc.id, employeeData);

          // Exclure l'utilisateur actuel
          if (doc.id === currentUser?.id) {
            return;
          }

          // Exclure les comptes bénéficiaires (email commence par "client_")
          // Ces utilisateurs seront ajoutés depuis la table clients
          if (employeeData.email && employeeData.email.startsWith('client_')) {
            console.log('Employé-Bénéficiaire exclu (sera ajouté depuis clients):', doc.id, employeeData.email);
            return;
          }

          // Récupérer le nom du rôle
          const roleId = employeeData.role;
          const roleName = rolesMap.get(roleId) || roleId || 'Collaborateur';

          // Inclure tous les employés normaux (intervenants)
          employees.push({
            id: doc.id,
            firstName: employeeData.firstName || '',
            lastName: employeeData.lastName || '',
            name: `${employeeData.firstName || ''} ${employeeData.lastName || ''}`.trim() || 'Utilisateur sans nom',
            role: roleName,
            roleId: roleId,
            type: 'intervenant',
            email: employeeData.email || '',
            isActive: employeeData.isActive !== false
          });
        });

        // Récupérer SEULEMENT les bénéficiaires qui ont un compte utilisateur
        if (beneficiaireUserMap.size > 0) {
          console.log('🔍 Recherche des bénéficiaires-utilisateurs dans la table clients (CreateGroupModal)...');
          const clientsQuery = query(collection(db, 'clients'));
          const clientsSnapshot = await getDocs(clientsQuery);

          console.log('Clients récupérés pour groupe (CreateGroupModal):', clientsSnapshot.size);

          let beneficiairesAdded = 0;
          clientsSnapshot.forEach(doc => {
            const clientData = doc.data();
            const clientIdLower = doc.id.toLowerCase();

            // Exclure l'utilisateur actuel (si c'est un bénéficiaire-utilisateur)
            if (doc.id === currentUser?.id) {
              return;
            }

            // Ajouter SEULEMENT les bénéficiaires qui ont un compte utilisateur
            if (beneficiaireUserMap.has(clientIdLower)) {
              const employeeId = beneficiaireUserMap.get(clientIdLower);
              console.log('✅ Bénéficiaire-Utilisateur ajouté depuis clients (CreateGroupModal):', doc.id, '(Employee ID:', employeeId, ')', clientData);
              employees.push({
                id: doc.id,
                firstName: clientData.firstName || '',
                lastName: clientData.lastName || '',
                name: `${clientData.firstName || ''} ${clientData.lastName || ''}`.trim() || clientData.name || 'Bénéficiaire',
                role: 'Bénéficiaire (Utilisateur)',
                type: 'beneficiaire',
                email: clientData.email || '',
                isActive: clientData.isActive !== false,
                hasUserAccount: true
              });
              beneficiairesAdded++;
            } else {
              console.log('❌ Client ignoré (pas de compte utilisateur - CreateGroupModal):', doc.id);
            }
          });
          console.log(`📊 Total bénéficiaires-utilisateurs ajoutés (CreateGroupModal): ${beneficiairesAdded}`);
        } else {
          console.log('⚠️ Aucun bénéficiaire-utilisateur détecté dans employees (CreateGroupModal)');
        }

        console.log('Employés disponibles pour groupe (CreateGroupModal):', employees.length);
        setAvailableEmployees(employees);
      } catch (error) {
        console.error('Erreur récupération employés:', error);
        setError('Impossible de charger la liste des employés');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [db, currentUser?.id]);

  const filteredEmployees = availableEmployees.filter(emp =>
    emp.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleParticipant = (employeeId) => {
    setSelectedParticipants(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    setError('');

    if (!groupName.trim()) {
      setError('Le nom du groupe est obligatoire');
      return;
    }

    if (selectedParticipants.length < 1) {
      setError('Sélectionnez au moins un participant');
      return;
    }

    setCreating(true);
    try {
      await createGroup(groupName.trim(), groupDescription.trim(), selectedParticipants);
      onClose();
    } catch (error) {
      console.error('Erreur création groupe:', error);
      setError('Impossible de créer le groupe. Veuillez réessayer.');
    } finally {
      setCreating(false);
    }
  };

  const getSelectedEmployeeNames = () => {
    return availableEmployees
      .filter(emp => selectedParticipants.includes(emp.id))
      .map(emp => emp.name);
  };

  return (
    <EnhancedModal onClose={onClose} size="xl">
      <ModalHeader>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${
            theme === 'dark' ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-100'
          }`}>
            <Users size={24} className="text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Créer un groupe</h2>
            <p className="text-sm opacity-70">Ajouter de nouveaux participants à la conversation</p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        <form onSubmit={handleCreateGroup} className="space-y-6">
          {/* Informations du groupe */}
          <div className="space-y-4">
            <div>
              <label htmlFor="groupName" className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Nom du groupe <span className="text-red-500">*</span>
              </label>
              <input
                id="groupName"
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Ex: Équipe du matin"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                }`}
                maxLength={50}
                required
              />
            </div>

            <div>
              <label htmlFor="groupDescription" className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Description (optionnelle)
              </label>
              <textarea
                id="groupDescription"
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                placeholder="Ex: Groupe pour coordonner les interventions du matin"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                }`}
                rows={3}
                maxLength={200}
              />
            </div>
          </div>

          {/* Sélection des participants */}
          <div className="space-y-4">
            <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
              Participants ({selectedParticipants.length} sélectionné{selectedParticipants.length > 1 ? 's' : ''})
            </h3>

            {/* Barre de recherche */}
            <div className="relative">
              <Search size={16} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Rechercher un participant..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                }`}
              />
            </div>

            {/* Liste des participants sélectionnés */}
            {selectedParticipants.length > 0 && (
              <div>
                <div className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Sélectionnés :</div>
                <div className="flex flex-wrap gap-2">
                  {getSelectedEmployeeNames().map((name, index) => (
                    <span key={index} className={`inline-flex items-center px-3 py-1 text-sm rounded-full ${
                      theme === 'dark'
                        ? 'bg-blue-900 text-blue-200'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Liste des employés */}
            <div className={`border rounded-lg max-h-60 overflow-y-auto ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              {loading ? (
                <div className={`flex justify-center items-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  <div>Chargement des participants...</div>
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className={`flex flex-col items-center justify-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  <User size={48} className={theme === 'dark' ? 'text-gray-600 mb-2' : 'text-gray-300 mb-2'} />
                  <h3 className={`text-lg font-medium mb-1 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Aucun participant trouvé</h3>
                  <p className="text-sm text-center">Aucun participant ne correspond à votre recherche.</p>
                </div>
              ) : (
                filteredEmployees.map(employee => {
                  const isSelected = selectedParticipants.includes(employee.id);
                  return (
                    <div
                      key={employee.id}
                      className={`flex items-center gap-3 p-3 cursor-pointer border-b last:border-b-0 ${
                        theme === 'dark'
                          ? `border-gray-700 ${isSelected ? 'bg-blue-900 bg-opacity-30' : 'hover:bg-gray-800'}`
                          : `border-gray-100 ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`
                      }`}
                      onClick={() => toggleParticipant(employee.id)}
                    >
                      <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                        isSelected
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : theme === 'dark'
                            ? 'border-gray-600'
                            : 'border-gray-300'
                      }`}>
                        {isSelected && <Check size={12} />}
                      </div>
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm">
                        <User size={16} />
                      </div>
                      <div className="flex-1">
                        <div className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{employee.name}</div>
                        <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{employee.role}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Erreur */}
          {error && (
            <div className={`p-3 text-sm border rounded-lg ${
              theme === 'dark'
                ? 'text-red-400 bg-red-900 bg-opacity-20 border-red-800'
                : 'text-red-600 bg-red-50 border-red-200'
            }`}>
              {error}
            </div>
          )}
        </form>
      </ModalBody>

      <ModalFooter>
        <ModalButton variant="secondary" onClick={onClose}>
          Annuler
        </ModalButton>
        <ModalButton
          variant="primary"
          onClick={handleCreateGroup}
          disabled={creating || !groupName.trim() || selectedParticipants.length === 0}
        >
          {creating ? 'Création...' : 'Créer le groupe'}
        </ModalButton>
      </ModalFooter>
    </EnhancedModal>
  );
};

export default CreateGroupModal;
